import classNames from 'classnames';
import React from 'react';

import * as OrganizationActions from 'actions/OrganizationActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import OrganizationPermissionConstants from 'constants/OrganizationPermissionConstants';
import EntityStore from 'stores/EntityStore';
import Avatar, {getAvatarHeight} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';
import fuzzydate from 'util/fuzzydate';
import * as modals from 'util/Modals';
import {capitalize} from 'util/NameUtil';

import * as OrganizationConstants from './OrganizationConstants';
import {OrganizationPermissionRole} from './OrganizationConstants';

const AVATAR_SIZE = AvatarSize.SMALL;
const {ADMIN, PERMISSION_LEVEL_TO_DISPLAY} = OrganizationPermissionConstants;

const NAME_COLUMN = 'Name';
const ID_COLUMN = 'ID';
const HANDLE_COLUMN = 'Handle';
const EMAIL_COLUMN = 'Email';
const ROLE_COLUMN = 'Role';
const LAST_SEEN_COLUMN = 'Last Seen';
const OVERFLOW_COLUMN = '';

type COLUMN_NAME =
  | typeof NAME_COLUMN
  | typeof ID_COLUMN
  | typeof HANDLE_COLUMN
  | typeof EMAIL_COLUMN
  | typeof ROLE_COLUMN
  | typeof LAST_SEEN_COLUMN
  | typeof OVERFLOW_COLUMN;

function NameCell({member}: {member: OrganizationConstants.Member}): JSX.Element {
  return (
    <div>
      <Avatar entity={member} size={AVATAR_SIZE} />
      <span className="u-small-spacing-left">{member.name}</span>
    </div>
  );
}

interface RoleCellProps {
  groupId: string;
  canManageRoles: boolean;
  isLoading: boolean;
  member: OrganizationConstants.Member;
}

class RoleCell extends React.Component<RoleCellProps> {
  _handleChangeRole(member: OrganizationConstants.Member, role: OrganizationPermissionRole): void {
    OrganizationActions.updateMemberPermission(this.props.groupId, member.id, role);
  }

  override render(): JSX.Element {
    const memberLevel = this.props.member.level;
    const displayName = PERMISSION_LEVEL_TO_DISPLAY[memberLevel as keyof typeof PERMISSION_LEVEL_TO_DISPLAY];
    if (!this.props.canManageRoles) {
      return <div>{displayName}</div>;
    }
    // Disable if member is loading or if current user is already admin.
    const isDisabled =
      this.props.isLoading ||
      (EntityStore.currentUser().id === this.props.member.id && this.props.member.level === ADMIN);
    return (
      <SelectDropdown
        displayName={displayName}
        disabled={isDisabled}
        menu={
          <Menu scrollable={false} width={Menu.WIDTHS.MEDIUM}>
            {Object.keys(PERMISSION_LEVEL_TO_DISPLAY).map(
              (level: keyof typeof PERMISSION_LEVEL_TO_DISPLAY) => (
                <Option
                  key={level}
                  onSelect={() => {
                    if (memberLevel !== parseInt(level, 10)) {
                      this._handleChangeRole(this.props.member, parseInt(level, 10));
                    }
                  }}
                >
                  {PERMISSION_LEVEL_TO_DISPLAY[level]}
                </Option>
              )
            )}
          </Menu>
        }
        dataTestAttrs={{component: 'RoleCellDropdown', element: 'selectDropdown'}}
      />
    );
  }
}

interface RemoveCellProps {
  groupId: string;
  groupType: string;
  canManageRoles: boolean;
  isLoading: boolean;
  member: OrganizationConstants.Member;
}

class RemoveCell extends React.Component<RemoveCellProps> {
  private _handleRemoveMember(isCurrentUser: boolean): void {
    const memberName = isCurrentUser ? 'yourself' : this.props.member.name;
    const uniqueIdentifier =
      this.props.member.type === ModelTypeConstants.USER ? this.props.member.email : this.props.member.id;
    const groupTypeText = capitalize(this.props.groupType);
    if (!this.props.isLoading) {
      modals
        .confirm(
          isCurrentUser ? `Leave ${groupTypeText}` : `Remove ${groupTypeText} Member`,
          `Clicking OK will remove ${memberName} (${uniqueIdentifier}) from the
        ${this.props.groupType}.`,
          'danger'
        )
        .done(() => {
          OrganizationActions.removeMember(this.props.groupId, this.props.member.id);
        });
    }
  }

  override render(): JSX.Element | null {
    const isCurrentUser = EntityStore.currentUser().id === this.props.member.id;
    if (!this.props.canManageRoles && !isCurrentUser) {
      return null;
    }
    return (
      <div className="tableRow--visibleOnHover-content">
        <Tooltip
          container="body"
          placement={TooltipPlacement.TOP}
          title={isCurrentUser ? `Leave ${capitalize(this.props.groupType)}` : 'Remove Member'}
        >
          <a
            className={classNames('u-faded', {
              'u-danger-pointer': !this.props.isLoading,
            })}
            onClick={(event) => {
              this._handleRemoveMember(isCurrentUser);
              event.stopPropagation();
            }}
          >
            <BenchlingIcon icon="format-delete" />
          </a>
        </Tooltip>
      </div>
    );
  }
}

interface BaseMembersTableProps {
  groupId: string;
  groupType: string;
  canManageRoles: boolean;
  members: Array<OrganizationConstants.Member>;
  shouldShowMemberInfo: boolean;
  userIdToMemberInfo: {[userId: string]: OrganizationConstants.MemberInfo};
  columnsToRender: Array<COLUMN_NAME>;
  onRowClick?: (row: {id: string}) => void;
}

class BaseMembersTable extends React.Component<BaseMembersTableProps> {
  private _getSortValue = (
    row: ScrollableTableRow & {extra: {member: OrganizationConstants.Member}},
    sortKey: string
  ): number | string => {
    const {member} = row.extra;
    switch (sortKey) {
      case 'memberLevel': {
        return PERMISSION_LEVEL_TO_DISPLAY[member.level as keyof typeof PERMISSION_LEVEL_TO_DISPLAY];
      }
      case 'lastSeen': {
        const lastSeen = this.props.userIdToMemberInfo[member.id]?.lastSeen;
        return lastSeen || '';
      }
      default: {
        // @ts-expect-error
        return member[sortKey];
      }
    }
  };

  private _getRowContentHeight = (): number => getAvatarHeight(AVATAR_SIZE);

  override render(): JSX.Element {
    const availableColumns: Array<{
      header: COLUMN_NAME;
      accessorKey: string;
      flexGrow?: number;
      isCollapsible: boolean;
      isSortable: boolean;
      sortKey?: string;
      width?: number;
    }> = [
      {
        header: NAME_COLUMN,
        accessorKey: 'name',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: true,
        sortKey: 'name',
      },
      {
        header: ID_COLUMN,
        accessorKey: 'id',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: true,
        sortKey: 'id',
      },
      {
        header: HANDLE_COLUMN,
        accessorKey: 'handle',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: true,
        sortKey: 'handle',
      },
    ];

    if (this.props.shouldShowMemberInfo) {
      availableColumns.push({
        header: EMAIL_COLUMN,
        accessorKey: 'email',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: true,
        sortKey: 'email',
      });
      availableColumns.push({
        header: ROLE_COLUMN,
        accessorKey: 'memberLevel',
        isCollapsible: false,
        isSortable: true,
        sortKey: 'memberLevel',
      });
      availableColumns.push({
        header: LAST_SEEN_COLUMN,
        accessorKey: 'lastSeen',
        isCollapsible: false,
        isSortable: true,
        sortKey: 'lastSeen',
      });
      availableColumns.push({
        header: OVERFLOW_COLUMN,
        accessorKey: 'remove',
        isCollapsible: false,
        isSortable: false,
        width: 83,
      });
    }

    const columns = availableColumns.filter((column) => this.props.columnsToRender.includes(column.header));

    const rows = this.props.members.map((member) => {
      // userIdToMemberInfo and members can be out of sync.
      const memberInfo = this.props.userIdToMemberInfo[member.id] || {
        lastSeen: null,
        loading: true,
      };
      const lastSeen = memberInfo.lastSeen
        ? fuzzydate(memberInfo.lastSeen, {days: 31, showTime: false})
        : null;
      return {
        id: member.id,
        data: {
          id: member.id,
          name: <NameCell member={member} />,
          handle: member.handle,
          email: this.props.shouldShowMemberInfo && (
            <a
              onClick={(event) => {
                event.stopPropagation();
              }}
              rel="noreferrer noopener"
              href={`mailto:${member.email}`}
            >
              {member.email}
            </a>
          ),
          memberLevel: this.props.shouldShowMemberInfo && (
            <RoleCell
              groupId={this.props.groupId}
              canManageRoles={this.props.canManageRoles}
              isLoading={memberInfo.loading}
              member={member}
            />
          ),
          lastSeen,
          remove: (
            <RemoveCell
              canManageRoles={this.props.canManageRoles}
              isLoading={memberInfo.loading}
              groupId={this.props.groupId}
              groupType={this.props.groupType}
              member={member}
            />
          ),
        },
        extra: {member},
      };
    });
    // For members, sort by role (primary) and name (secondary). For non-members, sort by name.
    const initialSort = this.props.shouldShowMemberInfo
      ? [{sortKey: 'memberLevel'}, {sortKey: 'name'}]
      : [{sortKey: 'name'}];

    return (
      <SortableScrollableTable
        columns={columns}
        getSortValue={this._getSortValue}
        hasLineBelowHeader={false}
        initialRows={rows}
        initialSort={initialSort}
        getRowContentHeight={this._getRowContentHeight}
        onRowClick={this.props.onRowClick}
      />
    );
  }
}

interface MembersTableProps {
  groupId: string;
  groupType: string;
  canManageRoles: boolean;
  members: Array<OrganizationConstants.Member>;
  shouldShowMemberInfo: boolean;
  userIdToMemberInfo: {[userId: string]: OrganizationConstants.MemberInfo};
  onRowClick?: (row: {id: string}) => void;
}

function MembersTable(props: MembersTableProps): JSX.Element {
  return (
    <BaseMembersTable
      columnsToRender={[
        NAME_COLUMN,
        HANDLE_COLUMN,
        EMAIL_COLUMN,
        ROLE_COLUMN,
        LAST_SEEN_COLUMN,
        OVERFLOW_COLUMN,
      ]}
      {...props}
    />
  );
}

export function BenchlingAppsTable(props: MembersTableProps): JSX.Element {
  return (
    <BaseMembersTable
      columnsToRender={[NAME_COLUMN, ID_COLUMN, ROLE_COLUMN, LAST_SEEN_COLUMN, OVERFLOW_COLUMN]}
      {...props}
    />
  );
}

export default MembersTable;
